import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowSquareOut, BookOpen, CaretDown, Lightning, Package, PencilSimple, Warning } from 'shared/icons';
import { FormatDateShort, FormatDateTimeBr } from '../../utils/date';
import { FormatMoney } from '../../utils/format';
import { MLB_URLS } from '../../utils/ml';
import CampaignItemCompetitors from './CampaignItemCompetitors';
import CampaignItemPromotions from './CampaignItemPromotions';
import CampaignItemFamily from './CampaignItemFamily';
import CampaignItemTracking from './CampaignItemTracking';
import {
    ATTENTION_LABELS,
    BUYBOX_BADGES,
    CardColumn,
    ComputeItemFees,
    DASH,
    FormatDecimalOrDash,
    FormatIntOrDash,
    IsNum,
    MetricRow,
    ML_STATUS_BADGES,
    PercentPrefix,
    Pill,
    TrendBadge,
} from './campaignCardShared';

/**
 * Card do Monitor Unificado de campanhas — 1 anúncio por linha.
 *
 * Layout (denso, referência FUTURE/campanha.html + abas Legacy):
 *   [thumb 90×90 c/ atalhos no hover] | header (título · MLB · SKU · badges)
 *                                     | 6 colunas: Preços · Campanha atual ·
 *                                       Catálogo/Sugestão · Estoques · Vendas 7d · Tarifas
 *   + painel INLINE expansível (sem modal): campanhas OU concorrência
 *
 * EXPANDIR é papel de links explícitos no fim da coluna Catálogo/Sugestão —
 * "Ver campanhas" e, em anúncio de catálogo, "Ver concorrência". Antes o header
 * inteiro era o gatilho, mas ele não dizia o que ia abrir e capturava cliques de
 * quem só queria selecionar o título. Os dois painéis são exclusivos: abrir os
 * dois empilhados afundaria os cards seguintes.
 *
 * A tabela de concorrência busca na API do ML ao montar, por isso só é montada
 * quando aberta — o monitor lista dezenas de cards por página.
 *
 * Consome 1:1 o contrato `GET /api/v2/campaigns/items`
 * (MARKDOWN-files/Fable/10-monitor-campanhas/CONTRATO-MONITOR.md).
 *
 * NADA é estimado aqui: tarifa/frete/estoque/vendas ausentes viram '—'. A versão
 * anterior deste card inventava tarifa (heurística por listing_type + fixa
 * hardcoded) — bug #5 do contrato, eliminado.
 */

// Botões de ação do card (estilo "btn-outline btn-sm" do Campaign Manager
// antigo): ícone + rótulo, sempre visíveis. Substituem os atalhos que ficavam
// escondidos sobre a foto e só apareciam no hover — o dono não os achava.
const ACTION_CLASS =
    'inline-flex items-center gap-1 rounded-md border border-mono-300 bg-white px-2 py-0.5 text-[11px] font-medium text-mono-700 transition-colors hover:border-mono-500 hover:text-black dark:border-mono-700 dark:bg-mono-950 dark:text-mono-300 dark:hover:border-mono-500 dark:hover:text-white';

/**
 * `initialPanel`: painel a abrir já na montagem ('historico', 'campanhas',
 * 'concorrencia', 'familia'). É o deep-link da notificação: o sino navega para
 * /campaigns?tab=items&item_id=MLB…&panel=historico, o monitor filtra pelo
 * anúncio e passa o painel para o card, que abre e rola até si. Sem isso o
 * clique na notificação levava à lista e parava — o dono tinha que achar o
 * card e clicar de novo.
 */
const CampaignItemCard = ({ item, initialPanel = null }) => {
    // `null` = recolhido. Os painéis são exclusivos: são leituras diferentes do
    // mesmo anúncio e abrir dois empilhados afundaria os cards seguintes.
    const [expandedPanel, setExpandedPanel] = useState(initialPanel);
    const articleRef = useRef(null);

    // Deep-link chegando DEPOIS da montagem (o usuário já estava no monitor e
    // clicou noutra notificação): abre o painel pedido e traz o card à vista.
    useEffect(() => {
        if (!initialPanel) {
            return;
        }
        setExpandedPanel(initialPanel);
        articleRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    }, [initialPanel]);

    const title = item.title || `Item ${item.item_id}`;
    const isCatalog = item.catalog_listing === true;
    const status = ML_STATUS_BADGES[item.ml_status] || { label: item.ml_status || 'Desconhecido', tone: 'mono' };
    const attentionLabel = item.attention_status
        ? ATTENTION_LABELS[item.attention_status] || item.attention_status
        : null;

    // O tooltip precisa responder as duas perguntas que o selo levanta: por que
    // falhou e de quando é o dado que estou vendo. Sem a segunda, "desatualizado"
    // não diz se é de 5 minutos ou de 5 dias atrás.
    const outdatedTitle = item.promotions_sync_error
        ? [
              `O Mercado Livre recusou a última consulta deste anúncio (${item.promotions_sync_error}).`,
              item.promotions_synced_at
                  ? `Os dados de campanha abaixo são de ${FormatDateTimeBr(item.promotions_synced_at)}.`
                  : 'Este anúncio nunca foi consultado com sucesso.',
          ].join(' ')
        : undefined;

    // Referência estável: alimenta o useMemo de ordenação da lista expansível.
    const campaigns = useMemo(() => (Array.isArray(item.campaigns) ? item.campaigns : []), [item.campaigns]);
    const campaignsCount = IsNum(item.campaigns_available_count)
        ? Number(item.campaigns_available_count)
        : campaigns.length;
    const hasCampaign = Boolean(item.campaign_id || item.campaign_name);
    const currentCampaign = campaigns.find((entry) => entry?.is_current === true) || null;
    const newCampaignsCount = IsNum(item.new_campaigns_count)
        ? Number(item.new_campaigns_count)
        : campaigns.filter((entry) => entry?.is_new === true).length;

    // SKU do anúncio (`seller_sku`): string vazia/só-espaço conta como ausente —
    // a pílula é omitida sem furar o layout do header.
    const sku = typeof item.sku === 'string' ? item.sku.trim() : IsNum(item.sku) ? String(item.sku) : '';

    const togglePanel = (panel) => setExpandedPanel((prev) => (prev === panel ? null : panel));

    // ── coluna 1: preços (contrato §coluna 1) ──
    const priceBase = IsNum(item.price_base) ? Number(item.price_base) : null;
    const priceConsumer = IsNum(item.price_consumer) ? Number(item.price_consumer) : null;
    const showStrikeBase = priceBase !== null && priceConsumer !== null && priceBase > priceConsumer;

    // ── coluna 3: catálogo / sugestão ──
    const buybox = BUYBOX_BADGES[item.buybox_status] || null;
    const isWinningBuybox = item.buybox_status === 'winning';
    const competitorsCount = IsNum(item.competitors_count) ? Number(item.competitors_count) : 0;
    const bestCampaign = item.best_campaign || null;

    // ── coluna 6: tarifas REAIS (fórmulas do contrato §Fórmulas; null → '—') ──
    // As contas moram em `campaignCardShared` porque a exportação CSV precisa
    // dos mesmos três derivados — duplicar a fórmula foi o bug #5 do contrato.
    const { feeFixed, shippingCost, feeMlDisplay, feeTotal, feeTotalPct, feesPending } = ComputeItemFees(item);

    // Página do anúncio: clicar na foto ou no título abre tudo sobre ele.
    const detailPath = `/campaigns/items/${encodeURIComponent(item.item_id)}`;

    return (
        <article
            ref={articleRef}
            className="overflow-hidden rounded-xl border border-mono-200 bg-white transition-colors hover:border-mono-400 dark:border-mono-800 dark:bg-mono-950 dark:hover:border-mono-600"
        >
            <div className="flex">
                {/* THUMB — define a altura do card (90px); clicar abre a página do anúncio */}
                <div className="flex w-[110px] shrink-0 items-center justify-center p-2.5">
                    <Link
                        to={detailPath}
                        title="Abrir a página do anúncio"
                        className="block h-[90px] w-[90px] shrink-0 overflow-hidden rounded-lg border border-mono-200 bg-white transition-colors hover:border-mono-500 dark:border-mono-800 dark:bg-mono-900"
                    >
                        {item.thumbnail ? (
                            <img src={item.thumbnail} alt="" className="h-full w-full object-cover" />
                        ) : (
                            <div className="flex h-full w-full items-center justify-center">
                                <Package size={28} className="text-mono-300 dark:text-mono-700" />
                            </div>
                        )}
                    </Link>
                </div>

                <div className="flex min-w-0 flex-1 flex-col">
                    {/* HEADER — título 13px, MLB, SKU, status, CATÁLOGO, atenção.
                        Voltou a ser PASSIVO: expandir agora é papel dos links
                        explícitos na coluna Catálogo/Sugestão. Um card inteiro
                        clicável sem rótulo não dizia o que ia abrir, e ainda
                        capturava cliques de quem só queria selecionar o título. */}
                    <div className="flex items-start gap-2 border-b border-mono-100 px-2 py-1 text-left dark:border-mono-900">
                        <div className="min-w-0 flex-1">
                            <Link
                                to={detailPath}
                                className="block truncate text-[13px] font-semibold text-black hover:underline dark:text-white"
                                title={`${title} — abrir a página do anúncio`}
                            >
                                {title}
                            </Link>
                            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                                <a
                                    href={MLB_URLS.search(item.item_id)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="font-mono text-[11px] text-danger-600 hover:underline dark:text-danger-400"
                                >
                                    {item.item_id}
                                </a>
                                {sku ? (
                                    <span
                                        className="max-w-[180px] truncate rounded-md bg-mono-100 px-1.5 py-px font-mono text-[11px] text-mono-700 dark:bg-mono-900 dark:text-mono-300"
                                        title={`SKU ${sku}`}
                                    >
                                        {sku}
                                    </span>
                                ) : null}
                                <Pill tone={status.tone} className="uppercase">
                                    {status.label}
                                </Pill>
                                {isCatalog ? (
                                    <Pill tone="info" className="uppercase" title="Anúncio competindo em catálogo">
                                        catálogo
                                    </Pill>
                                ) : null}
                                {/* FAMÍLIA — o ML mostra lista geral + cópia de catálogo
                                    como um só anúncio ("sincronizado com #MLB…"); aqui são
                                    dois cards, cada um com sua campanha e seu rebate. O selo
                                    diz qual é o par e abre a comparação lado a lado. */}
                                {item.related_item_id ? (
                                    <button
                                        type="button"
                                        onClick={() => togglePanel('familia')}
                                        aria-expanded={expandedPanel === 'familia'}
                                        title={`Sincronizado com ${item.related_item_id}, a versão de ${isCatalog ? 'lista geral' : 'catálogo'} deste mesmo anúncio. Clique para comparar campanhas e rebates dos dois.`}
                                        className="inline-flex max-w-[220px] items-center gap-1 truncate rounded-md border border-info-200 bg-info-soft px-1.5 py-px font-mono text-[11px] text-info-700 transition-colors hover:border-info-400 dark:border-info-900 dark:bg-info-900/30 dark:text-info-300"
                                    >
                                        {/* O par é sempre um de cada: em card de catálogo o vínculo
                                            aponta para a versão de lista, e vice-versa. */}
                                        <span className="font-sans text-[10px] uppercase">{isCatalog ? 'lista' : 'catálogo'}</span>
                                        {item.related_item_id}
                                    </button>
                                ) : null}
                                {attentionLabel ? (
                                    <Pill tone="danger" icon={Warning} title="Requer atenção">
                                        {attentionLabel}
                                    </Pill>
                                ) : null}
                                {/* DESATUALIZADO — o Mercado Livre recusou a última
                                    consulta DESTE anúncio, então o que aparece nas
                                    colunas de campanha é a última leitura boa e pode
                                    estar velha. Fica no fim da linha de selos, depois
                                    de status/catálogo/atenção, porque é um aviso sobre
                                    a CONFIABILIDADE do resto — não um atributo do
                                    anúncio. Sem isto a tela mostrava dado de dias
                                    atrás como se fosse fresco. */}
                                {item.promotions_sync_error ? (
                                    <Pill tone="danger" className="uppercase" title={outdatedTitle}>
                                        desatualizado
                                    </Pill>
                                ) : null}
                                {newCampaignsCount > 0 ? (
                                    <Pill
                                        tone="info"
                                        className="uppercase"
                                        title={`${newCampaignsCount} campanha(s) apareceram para este anúncio desde a última vez que a aba Campanhas foi aberta`}
                                    >
                                        {newCampaignsCount} nova{newCampaignsCount > 1 ? 's' : ''}
                                    </Pill>
                                ) : null}
                            </div>
                        </div>

                        {/* AÇÕES — visíveis, com rótulo. Todas abrem o Mercado Livre em
                            nova aba, menos "Detalhes", que é a página interna. */}
                        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1 pt-0.5">
                            <Link to={detailPath} className={ACTION_CLASS} title="Tudo sobre este anúncio: campanhas, histórico, estoque, vendas">
                                Detalhes
                            </Link>
                            <a
                                href={MLB_URLS.promos(item.item_id)}
                                target="_blank"
                                rel="noreferrer"
                                className={ACTION_CLASS}
                                title="Central de promoções deste anúncio no Mercado Livre"
                            >
                                <Lightning size={12} /> Campanhas ML
                            </a>
                            <a
                                href={MLB_URLS.edit(item.item_id)}
                                target="_blank"
                                rel="noreferrer"
                                className={ACTION_CLASS}
                                title="Editar o anúncio no Seller Center"
                            >
                                <PencilSimple size={12} /> Editar
                            </a>
                            {item.permalink ? (
                                <a href={item.permalink} target="_blank" rel="noreferrer" className={ACTION_CLASS} title="Página pública do anúncio">
                                    <ArrowSquareOut size={12} /> Anúncio
                                </a>
                            ) : null}
                            {isCatalog ? (
                                <a
                                    href={MLB_URLS.competition(item.item_id)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className={ACTION_CLASS}
                                    title="Competição de catálogo no Mercado Livre"
                                >
                                    <BookOpen size={12} /> Catálogo
                                </a>
                            ) : null}
                        </div>
                    </div>

                    {/* 6 COLUNAS */}
                    <div className="grid flex-1 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
                        {/* 1 — PREÇOS. Todo percentual é PREFIXO (rebate % e desconto %);
                            os valores em R$ ficam brancos — cor só nos percentuais. */}
                        <CardColumn>
                            <MetricRow
                                label="Base"
                                value={FormatMoney(priceBase)}
                                valueClass={`font-normal text-mono-500 dark:text-mono-400 ${showStrikeBase ? 'line-through' : ''}`}
                                title="Preço cheio (original_price)"
                            />
                            <MetricRow
                                label="Consumer"
                                value={FormatMoney(
                                    item.boosted_offer && IsNum(item.price_consumer_final)
                                        ? item.price_consumer_final
                                        : priceConsumer,
                                )}
                                title={
                                    item.boosted_offer && IsNum(item.price_consumer_final)
                                        ? `Preço final com desconto automático do Meli (oferta base: ${FormatMoney(priceConsumer)})`
                                        : 'O que o comprador paga hoje'
                                }
                            />
                            <MetricRow
                                label="Rebate"
                                prefix={
                                    <PercentPrefix
                                        value={item.rebate_percent}
                                        title="Percentual de rebate sobre o preço base"
                                    />
                                }
                                value={IsNum(item.rebate_amount) ? FormatMoney(item.rebate_amount) : DASH}
                                title="Subsídio do Meli sobre o preço base"
                            />
                            {/* Desconto migrou da pílula da coluna 2 pra cá: mesma
                                gramática do rebate (percentual antes do valor), tudo
                                no mesmo lugar. `invertColor`: desconto maior = pior. */}
                            <MetricRow
                                label="Líquido"
                                prefix={
                                    <PercentPrefix
                                        value={currentCampaign?.discount_percent}
                                        invertColor
                                        title="Desconto sobre o preço base"
                                    />
                                }
                                value={FormatMoney(item.price_net)}
                                title="Consumer + rebate (não desconta tarifa/frete)"
                            />
                            {/* Boost do Meli. Só aparece quando a API informou:
                                a doc condiciona os campos a `boosted_offer: true`,
                                e uma linha fixa em branco sugeriria "sem boost"
                                onde na verdade o dado nunca foi consultado.
                                Fora do Rebate de propósito — a doc diz que o
                                percentual do boost é INDEPENDENTE do rebate. */}
                            {item.boosted_offer && IsNum(item.boost_amount) ? (
                                <MetricRow
                                    label="Boost ML"
                                    prefix={
                                        <PercentPrefix
                                            value={item.boost_percent}
                                            title="Desconto automático do Meli, devolvido como redução no custo por venda"
                                        />
                                    }
                                    value={FormatMoney(item.boost_amount)}
                                    title={`Líquido com boost: ${FormatMoney(item.price_net_with_boost)}`}
                                />
                            ) : null}
                        </CardColumn>

                        {/* 2 — CAMPANHA ATUAL */}
                        <CardColumn label="Campanha atual">
                            {hasCampaign ? (
                                <>
                                    {/* Nome em BRANCO: nas 6 colunas a cor é reservada aos
                                        percentuais (regra do dono). */}
                                    <p
                                        className="truncate text-[11px] font-semibold text-black dark:text-white"
                                        title={item.campaign_name || item.campaign_id}
                                    >
                                        {item.campaign_name || item.campaign_id}
                                    </p>
                                    <p className="truncate text-[9px] uppercase text-mono-400">
                                        {item.campaign_type || DASH} · {item.campaign_item_status || DASH}
                                    </p>
                                    {/* Sem pílulas aqui: o rebate e o desconto viraram
                                        prefixos percentuais na coluna de Preços (tudo no
                                        mesmo lugar), e os atalhos do ML vivem no thumbnail. */}
                                </>
                            ) : (
                                <p className="text-[11px] italic text-mono-400">Sem campanha ativa</p>
                            )}
                        </CardColumn>

                        {/* 3 — CATÁLOGO / SUGESTÃO (dinâmica).
                            O contador de campanhas elegíveis mora AQUI, como "(x)" no
                            título — saiu do affordance do header. Sem ícones. Só os
                            casos RESOLVIDOS ("Ganhando" / "Já está na melhor") são
                            verdes e 2px maiores; o resto fica neutro. */}
                        <CardColumn
                            label={`${isCatalog ? 'Catálogo' : 'Sugestão'} (${FormatIntOrDash(campaignsCount)})`}
                            labelTitle={`${FormatIntOrDash(campaignsCount)} campanha(s) elegível(is) — clique no header para ver a lista`}
                        >
                            {/* CATÁLOGO: só o status. "P/ ganhar" e "Líder" saíram —
                                são dados de disputa e agora vivem na tabela de
                                concorrência, junto do resto do contexto (quem é o
                                líder, por quanto, com que frete). */}
                            {isCatalog ? (
                                buybox ? (
                                    <Pill tone={isWinningBuybox ? 'success' : 'neutral'} className="uppercase">
                                        {buybox.label}
                                    </Pill>
                                ) : (
                                    <span className="text-[10px] text-mono-400">{DASH}</span>
                                )
                            ) : bestCampaign ? (
                                // Sugestão de troca/entrada NÃO é caso resolvido → neutro.
                                <Pill
                                    tone="neutral"
                                    title={`${hasCampaign ? 'Melhor campanha' : 'Entrar em'}: ${bestCampaign.campaign_name || bestCampaign.campaign_id}`}
                                >
                                    {hasCampaign ? 'Melhor' : 'Entrar em'}:{' '}
                                    {bestCampaign.campaign_name || bestCampaign.campaign_id}
                                </Pill>
                            ) : item.is_on_best_campaign === true ? (
                                <Pill tone="success">JÁ ESTÁ NA MELHOR</Pill>
                            ) : (
                                <span className="text-[10px] text-mono-400">{DASH}</span>
                            )}

                            {/* O GATILHO de expandir mora aqui, no fim da coluna, e
                                diz o que vai abrir — antes era o header inteiro, mudo.
                                Catálogo abre a concorrência; sem catálogo, as campanhas. */}
                            <button
                                type="button"
                                onClick={() => togglePanel(isCatalog ? 'concorrencia' : 'campanhas')}
                                aria-expanded={expandedPanel === (isCatalog ? 'concorrencia' : 'campanhas')}
                                title={
                                    isCatalog
                                        ? 'Ver quem disputa este catálogo, com preços e fretes'
                                        : 'Ver todas as campanhas elegíveis deste anúncio'
                                }
                                className="mt-1 inline-flex w-fit items-center gap-0.5 text-[10px] font-semibold text-info-600 transition-colors hover:underline dark:text-info-400"
                            >
                                {isCatalog
                                    ? `Ver concorrência${competitorsCount > 0 ? ` (${FormatIntOrDash(competitorsCount)})` : ''}`
                                    : 'Ver campanhas'}
                                <CaretDown
                                    size={11}
                                    aria-hidden="true"
                                    className={`transition-transform ${
                                        expandedPanel === (isCatalog ? 'concorrencia' : 'campanhas')
                                            ? 'rotate-180'
                                            : ''
                                    }`}
                                />
                            </button>

                            {/* Anúncio de catálogo TAMBÉM tem campanhas: sem este
                                segundo link elas ficariam inalcançáveis nele. */}
                            {isCatalog && campaignsCount > 0 ? (
                                <button
                                    type="button"
                                    onClick={() => togglePanel('campanhas')}
                                    aria-expanded={expandedPanel === 'campanhas'}
                                    title="Ver todas as campanhas elegíveis deste anúncio"
                                    className="inline-flex w-fit items-center gap-0.5 text-[10px] font-semibold text-info-600 transition-colors hover:underline dark:text-info-400"
                                >
                                    Ver campanhas
                                    <CaretDown
                                        size={11}
                                        aria-hidden="true"
                                        className={`transition-transform ${expandedPanel === 'campanhas' ? 'rotate-180' : ''}`}
                                    />
                                </button>
                            ) : null}

                            {/* HISTÓRICO — entrou/mudou/saiu de campanha ao longo do
                                tempo. Sempre disponível: anúncio sem campanha hoje pode
                                ter saído de uma ontem, e é isso que se quer ver. */}
                            <button
                                type="button"
                                onClick={() => togglePanel('historico')}
                                aria-expanded={expandedPanel === 'historico'}
                                title="Ver o histórico de entradas, saídas e mudanças de campanha deste anúncio"
                                className="inline-flex w-fit items-center gap-0.5 text-[10px] font-semibold text-info-600 transition-colors hover:underline dark:text-info-400"
                            >
                                Ver histórico
                                <CaretDown
                                    size={11}
                                    aria-hidden="true"
                                    className={`transition-transform ${expandedPanel === 'historico' ? 'rotate-180' : ''}`}
                                />
                            </button>
                        </CardColumn>

                        {/* 4 — ESTOQUES (valores brancos: são contagens, não percentuais)
                            "Anúncio" é o disponível DESTE MLB; "Full do SKU" é o
                            saldo do galpão do ML somado por SKU. Ficam lado a lado
                            porque são números diferentes de propósito: dois anúncios
                            irmãos do mesmo SKU dividem o Full mas têm anúncio
                            próprio. Antes só aparecia o Full, rotulado como se
                            fosse do anúncio — e os irmãos exibiam o mesmo número. */}
                        <CardColumn label="Estoques">
                            <MetricRow label="Anúncio" value={FormatIntOrDash(item.stock_ml_available)} />
                            <MetricRow label="Full do SKU" value={FormatIntOrDash(item.stock_sku_fulfillment)} />
                            <MetricRow label="ML indisponível" value={FormatIntOrDash(item.stock_ml_unavailable)} />
                            <MetricRow label="Bling" value={FormatIntOrDash(item.stock_bling)} />
                        </CardColumn>

                        {/* 5 — VENDAS (últ. 7 dias) */}
                        <CardColumn label="Vendas (últ. 7 dias)">
                            <MetricRow label="Últimas 24h" value={FormatIntOrDash(item.sales_24h)} />
                            <MetricRow label="Média/dia" value={FormatDecimalOrDash(item.sales_7d_avg)} />
                            {/* A data usa o MESMO corpo dos percentuais-prefixo (9px),
                                não o do valor: ela qualifica o número, como o
                                percentual faz nas outras linhas. */}
                            <MetricRow
                                label="Melhor dia"
                                prefix={
                                    item.sales_7d_best_day?.date ? (
                                        <span className="shrink-0 text-[9px] font-semibold tabular-nums text-mono-400">
                                            {FormatDateShort(item.sales_7d_best_day.date)}
                                        </span>
                                    ) : null
                                }
                                value={FormatIntOrDash(item.sales_7d_best_day?.qty)}
                            />
                            {/* Variação semanal como PREFIXO (era sufixo) e sem ícone. */}
                            <MetricRow
                                label="Total"
                                prefix={<TrendBadge percent={item.sales_trend_percent} />}
                                value={FormatIntOrDash(item.sales_7d_total)}
                            />
                        </CardColumn>

                        {/* 6 — TARIFAS (reais; null → '—', nunca estimado) */}
                        <CardColumn
                            label="Tarifas"
                            sub={feesPending ? 'Aguardando sync' : null}
                            className={feesPending ? 'opacity-80' : ''}
                        >
                            {/* Percentual da tarifa ML é PREFIXO do valor (era sufixo no
                                rótulo). `invertColor`: tarifa maior = pior. */}
                            <MetricRow
                                label="ML"
                                prefix={
                                    <PercentPrefix
                                        value={item.fee_percentage}
                                        invertColor
                                        title="Percentual da tarifa ML"
                                    />
                                }
                                value={FormatMoney(feeMlDisplay)}
                                title={
                                    item.fees_synced_at
                                        ? `Tarifa ML sincronizada em ${FormatDateTimeBr(item.fees_synced_at)}`
                                        : 'Tarifa ainda não sincronizada'
                                }
                            />
                            <MetricRow label="Fixa" value={FormatMoney(feeFixed)} />
                            <MetricRow label="Frete" value={FormatMoney(shippingCost)} />
                            {/* Total: percentual sobre o preço vem ANTES do valor.
                                Sem linha divisória — era a única do card e destoava;
                                o negrito do valor já separa o total dos itens. */}
                            <div className="mt-px flex items-baseline justify-between gap-1.5 leading-tight">
                                <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wide text-mono-500">
                                    Total
                                </span>
                                <span className="flex min-w-0 items-baseline justify-end gap-1">
                                    <PercentPrefix
                                        value={feeTotalPct}
                                        invertColor
                                        title="Tarifa + frete sobre o preço ao consumidor"
                                    />
                                    <span className="min-w-0 truncate text-right text-[11px] font-bold tabular-nums text-black dark:text-white">
                                        {FormatMoney(feeTotal)}
                                    </span>
                                </span>
                            </div>
                        </CardColumn>
                    </div>
                </div>
            </div>

            {/* PAINÉIS EXPANSÍVEIS — nenhum modal. Um de cada vez (ver estado). */}
            {expandedPanel ? (
                <div className="border-t border-mono-100 bg-mono-50 p-2 dark:border-mono-900 dark:bg-mono-900/40">
                    {expandedPanel === 'campanhas' ? (
                        <CampaignItemPromotions campaigns={campaigns} />
                    ) : expandedPanel === 'familia' ? (
                        <CampaignItemFamily itemId={item.item_id} />
                    ) : expandedPanel === 'historico' ? (
                        <CampaignItemTracking itemId={item.item_id} />
                    ) : (
                        // Monta só quando aberto: o componente busca na API do ML ao
                        // montar, e o monitor lista dezenas de cards por página.
                        <CampaignItemCompetitors itemId={item.item_id} />
                    )}
                </div>
            ) : null}
        </article>
    );
};

export default CampaignItemCard;
